/**
 * Derives a stable category slug for the auth app's /error page.
 *
 * The /error page self-reports every failure under a single `AuthAppError`
 * fingerprint, so unrelated failures (expired Hydra challenges, logout
 * whitelist misconfig, Kratos flow errors, JWS parse bugs) collapse into one
 * PostHog issue and new regressions hide in the noise. Deriving a category
 * from the page's query params lets the caller split the fingerprint per
 * failure class and expose a queryable `auth_error_category` property.
 *
 * Precedence: `error_description` (Hydra) → `title` → `message` → bare `id`
 * (Kratos flow error id) → `unknown`.
 */

export interface AuthErrorQuery {
  error_description?: string | string[] | null
  title?: string | string[] | null
  message?: string | string[] | null
  id?: string | string[] | null
}

const first = (value?: string | string[] | null): string | undefined => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value[0] : undefined
  }
  return value ?? undefined
}

// Known Hydra `error_description` substrings mapped to stable slugs. Matched
// case-insensitively against the raw description. Order matters only in that
// the first match wins.
const HYDRA_DESCRIPTION_MATCHERS: readonly {
  match: string
  slug: string
}[] = [
  { match: 'compact jws format', slug: 'gojose_compact_jws' },
  { match: 'id_token_hint is missing', slug: 'logout_state_no_id_token_hint' },
  {
    match: 'post_logout_redirect_uri is not a whitelisted',
    slug: 'logout_redirect_not_whitelisted'
  },
  {
    match: 'not a whitelisted as a post_logout_redirect_uri',
    slug: 'logout_redirect_not_whitelisted'
  },
  {
    match: 'does not match any of the oauth 2.0 client',
    slug: 'oauth_redirect_uri_mismatch'
  },
  { match: 'invalid character', slug: 'hydra_json_parse_error' }
]

// Turns arbitrary human text into a short, stable slug: lower-cased, first few
// words, non-alphanumerics collapsed to underscores. Used as the fallback for
// unrecognised descriptions/titles/messages so related copy still buckets
// together without exploding into per-string issues.
const slugify = (text: string, maxWords = 4): string => {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .split('_')
    .filter(Boolean)
    .slice(0, maxWords)
    .join('_')

  return slug || 'unknown'
}

const categorizeHydraDescription = (description: string): string => {
  const haystack = description.toLowerCase()
  const known = HYDRA_DESCRIPTION_MATCHERS.find(({ match }) =>
    haystack.includes(match)
  )
  return known ? known.slug : slugify(description)
}

/**
 * Returns a stable category slug for an /error page render. Never throws; the
 * worst case is `'unknown'`.
 */
export const deriveAuthErrorCategory = (query: AuthErrorQuery): string => {
  const errorDescription = first(query.error_description)
  if (errorDescription) {
    return categorizeHydraDescription(errorDescription)
  }

  const title = first(query.title)
  if (title) {
    return slugify(title)
  }

  const message = first(query.message)
  if (message) {
    return slugify(message)
  }

  const id = first(query.id)
  if (id) {
    return 'kratos_error_id'
  }

  return 'unknown'
}
